import * as React from "react"
import { cn } from "../../lib/utils"
import { CalendarIcon, X, ChevronDownIcon } from "lucide-react"
import { Button } from "./button"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Label } from "./label"
import { Switch } from "./switch"

export interface DateRangePickerProps {
  onUpdate?: (values: {
    dateFrom: Date | undefined
    dateTo: Date | undefined
    compareFrom: Date | undefined
    compareTo: Date | undefined
  }) => void
  initialDateFrom?: Date | string
  initialDateTo?: Date | string
  initialCompareFrom?: Date | string
  initialCompareTo?: Date | string
  align?: "start" | "center" | "end"
  locale?: string
  showCompare?: boolean
  /** When true, adds time inputs for from/to (default start 00:00, end 23:59). */
  showTime?: boolean
  className?: string
}

function parseLocalDate(value?: Date | string): Date | undefined {
  if (!value) return undefined
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value
  }
  // Prefer YYYY-MM-DD as local calendar date to avoid UTC day-shift
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (match) {
    const year = Number(match[1])
    const month = Number(match[2]) - 1
    const day = Number(match[3])
    return new Date(year, month, day)
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

function toDateInputValue(date: Date | undefined): string {
  if (!date) return ""
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

function toDisplayDate(date: Date | undefined): string {
  if (!date) return ""
  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

function parseDdMmYyyy(value: string): Date | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined

  const match = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(trimmed)
  if (!match) return undefined

  const day = Number(match[1])
  const month = Number(match[2]) - 1
  const year = Number(match[3])
  const parsed = new Date(year, month, day)
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month ||
    parsed.getDate() !== day
  ) {
    return undefined
  }
  return parsed
}

interface DdMmYyyyDateInputProps {
  id: string
  value: Date | undefined
  onChange: (date: Date | undefined) => void
  disabled?: boolean
  className?: string
}

function DdMmYyyyDateInput({
  id,
  value,
  onChange,
  disabled,
  className,
}: DdMmYyyyDateInputProps) {
  const nativeRef = React.useRef<HTMLInputElement>(null)
  const [text, setText] = React.useState(toDisplayDate(value))

  React.useEffect(() => {
    setText(toDisplayDate(value))
  }, [value])

  const commitText = (raw: string) => {
    if (!raw.trim()) {
      onChange(undefined)
      return
    }
    const parsed = parseDdMmYyyy(raw)
    if (parsed) {
      onChange(parsed)
      setText(toDisplayDate(parsed))
    } else {
      setText(toDisplayDate(value))
    }
  }

  return (
    <div className="relative">
      <input
        type="text"
        id={id}
        inputMode="numeric"
        placeholder="dd/mm/yyyy"
        aria-label="Select date"
        title="Select date (dd/mm/yyyy)"
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commitText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            commitText(text)
          }
        }}
        className={cn(
          "flex h-8 w-full rounded-md border border-input bg-background py-1 pl-2 pr-8 text-xs",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0",
          disabled && "opacity-50",
          className
        )}
      />
      <input
        ref={nativeRef}
        type="date"
        tabIndex={-1}
        aria-hidden
        className="sr-only"
        value={toDateInputValue(value)}
        onChange={(e) => {
          const parsed = parseLocalDate(e.target.value || undefined)
          if (parsed) onChange(parsed)
        }}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => nativeRef.current?.showPicker()}
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 hover:bg-gray-100 disabled:opacity-50"
        title="Open calendar"
      >
        <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    </div>
  )
}

function toTimeInputValue(date: Date | undefined): string {
  if (!date) return ""
  const hours = String(date.getHours()).padStart(2, "0")
  const minutes = String(date.getMinutes()).padStart(2, "0")
  return `${hours}:${minutes}`
}

function mergeDateWithTime(
  existing: Date | undefined,
  dateValue: string | undefined,
  timeValue: string | undefined,
  defaultHours: number,
  defaultMinutes: number
): Date | undefined {
  if (!dateValue) return undefined

  const base = parseLocalDate(dateValue)
  if (!base) return undefined

  const timeSource = timeValue || (existing ? toTimeInputValue(existing) : "")
  const [hoursRaw, minutesRaw] = timeSource.split(":")
  const hours = Number(hoursRaw)
  const minutes = Number(minutesRaw)

  base.setHours(
    Number.isFinite(hours) ? hours : defaultHours,
    Number.isFinite(minutes) ? minutes : defaultMinutes,
    0,
    0
  )
  return base
}

function applyTimeToDate(
  existing: Date | undefined,
  timeValue: string | undefined,
  defaultHours: number,
  defaultMinutes: number
): Date | undefined {
  if (!existing || !timeValue) return existing

  const next = new Date(existing)
  const [hoursRaw, minutesRaw] = timeValue.split(":")
  const hours = Number(hoursRaw)
  const minutes = Number(minutesRaw)
  next.setHours(
    Number.isFinite(hours) ? hours : defaultHours,
    Number.isFinite(minutes) ? minutes : defaultMinutes,
    0,
    0
  )
  return next
}

const DateRangePicker = React.forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      onUpdate,
      initialDateFrom,
      initialDateTo,
      initialCompareFrom,
      initialCompareTo,
      align = "end",
      showCompare = true,
      showTime = false,
      className,
    },
    ref
  ) => {
    const [dateFrom, setDateFrom] = React.useState<Date | undefined>(
      parseLocalDate(initialDateFrom)
    )
    const [dateTo, setDateTo] = React.useState<Date | undefined>(
      parseLocalDate(initialDateTo)
    )
    const [compareFrom, setCompareFrom] = React.useState<Date | undefined>(
      parseLocalDate(initialCompareFrom)
    )
    const [compareTo, setCompareTo] = React.useState<Date | undefined>(
      parseLocalDate(initialCompareTo)
    )
    const [isCompare, setIsCompare] = React.useState(false)
    const onUpdateRef = React.useRef(onUpdate)
    const isFirstSyncRef = React.useRef(true)

    React.useEffect(() => {
      onUpdateRef.current = onUpdate
    }, [onUpdate])

    React.useEffect(() => {
      setDateFrom(parseLocalDate(initialDateFrom))
      setDateTo(parseLocalDate(initialDateTo))
    }, [initialDateFrom, initialDateTo])

    React.useEffect(() => {
      // Skip the initial mount notification; parent already owns these values
      if (isFirstSyncRef.current) {
        isFirstSyncRef.current = false
        return
      }
      onUpdateRef.current?.({
        dateFrom,
        dateTo,
        compareFrom: isCompare ? compareFrom : undefined,
        compareTo: isCompare ? compareTo : undefined,
      })
    }, [dateFrom, dateTo, compareFrom, compareTo, isCompare])

    const formatDate = (date: Date | undefined) => {
      if (!date) return ""
      const datePart = toDisplayDate(date)
      if (!showTime) return datePart
      const hours = date.getHours()
      const minutes = date.getMinutes().toString().padStart(2, "0")
      const ampm = hours >= 12 ? "PM" : "AM"
      const displayHours = hours % 12 || 12
      return `${datePart} ${displayHours}:${minutes} ${ampm}`
    }

    const setDatePreservingTime = (
      current: Date | undefined,
      nextDate: Date | undefined,
      defaultHours: number,
      defaultMinutes: number
    ): Date | undefined => {
      if (!nextDate) return undefined
      return mergeDateWithTime(
        current,
        toDateInputValue(nextDate),
        current ? toTimeInputValue(current) : `${String(defaultHours).padStart(2, "0")}:${String(defaultMinutes).padStart(2, "0")}`,
        defaultHours,
        defaultMinutes
      )
    }

    const getDateRangeText = () => {
      if (!dateFrom && !dateTo) return "Select date range"
      if (dateFrom && !dateTo) return `From ${formatDate(dateFrom)}`
      if (dateFrom && dateTo) {
        return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`
      }
      return "Select date range"
    }

    const handleClear = () => {
      setDateFrom(undefined)
      setDateTo(undefined)
      setCompareFrom(undefined)
      setCompareTo(undefined)
      setIsCompare(false)
    }

    return (
      <div className={cn("relative", className)} ref={ref}>
        <Popover>
          <PopoverTrigger>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                "h-9 rounded-md border border-[#E2E5EA] bg-white px-3 py-2 text-[13px]",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0",
                className
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              <span className="flex-1">{getDateRangeText()}</span>
              <ChevronDownIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align={align}>
            <div className="p-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{showTime ? "Date & Time Range" : "Date Range"}</Label>
                  {showTime ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
                        <div>
                          <Label htmlFor="date-from" className="text-xs">
                            From
                          </Label>
                          <DdMmYyyyDateInput
                            id="date-from"
                            value={dateFrom}
                            onChange={(nextDate) =>
                              setDateFrom(setDatePreservingTime(dateFrom, nextDate, 0, 0))
                            }
                          />
                        </div>
                        <div className="sm:pt-5">
                          <Label htmlFor="time-from" className="text-xs sm:sr-only">
                            From time
                          </Label>
                          <input
                            type="time"
                            id="time-from"
                            aria-label="Select start time"
                            title="Select start time"
                            value={toTimeInputValue(dateFrom) || "00:00"}
                            disabled={!dateFrom}
                            onChange={(e) =>
                              setDateFrom(applyTimeToDate(dateFrom, e.target.value, 0, 0))
                            }
                            className={cn(
                              "flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs sm:w-[120px]",
                              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0",
                              !dateFrom && "opacity-50"
                            )}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
                        <div>
                          <Label htmlFor="date-to" className="text-xs">
                            To
                          </Label>
                          <DdMmYyyyDateInput
                            id="date-to"
                            value={dateTo}
                            onChange={(nextDate) =>
                              setDateTo(setDatePreservingTime(dateTo, nextDate, 23, 59))
                            }
                          />
                        </div>
                        <div className="sm:pt-5">
                          <Label htmlFor="time-to" className="text-xs sm:sr-only">
                            To time
                          </Label>
                          <input
                            type="time"
                            id="time-to"
                            aria-label="Select end time"
                            title="Select end time"
                            value={toTimeInputValue(dateTo) || "23:59"}
                            disabled={!dateTo}
                            onChange={(e) =>
                              setDateTo(applyTimeToDate(dateTo, e.target.value, 23, 59))
                            }
                            className={cn(
                              "flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs sm:w-[120px]",
                              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0",
                              !dateTo && "opacity-50"
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="date-from" className="text-xs">
                          From
                        </Label>
                        <DdMmYyyyDateInput
                          id="date-from"
                          value={dateFrom}
                          onChange={setDateFrom}
                        />
                      </div>
                      <div>
                        <Label htmlFor="date-to" className="text-xs">
                          To
                        </Label>
                        <DdMmYyyyDateInput
                          id="date-to"
                          value={dateTo}
                          onChange={setDateTo}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {showCompare && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="compare-mode"
                        checked={isCompare}
                        onCheckedChange={setIsCompare}
                      />
                      <Label htmlFor="compare-mode" className="text-sm">
                        Compare to previous period
                      </Label>
                    </div>
                    {isCompare && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="compare-from" className="text-xs">
                            Compare From
                          </Label>
                          <DdMmYyyyDateInput
                            id="compare-from"
                            value={compareFrom}
                            onChange={setCompareFrom}
                          />
                        </div>
                        <div>
                          <Label htmlFor="compare-to" className="text-xs">
                            Compare To
                          </Label>
                          <DdMmYyyyDateInput
                            id="compare-to"
                            value={compareTo}
                            onChange={setCompareTo}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClear}
                    className="text-xs"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)

DateRangePicker.displayName = "DateRangePicker"

export { DateRangePicker }
